import React from 'react'
import { Button, GridItem } from '@deal/components'
import { generateGrid } from '#src/services/grid'
import { Link, withRouter, RouteComponentProps } from 'react-router-dom'
import { WithAnalyticsProps, withAnalytics } from '#src/containers/Analytics'
import {
  DepartmentLeadFragment,
  SocialMediaType,
  SetConsumerInitialLeadActionComponent,
  SetConsumerInitialLeadActionMutationFn,
  ConsumerInitialLeadAction
} from '#src/generated/graphql'
import { isExpertPresent } from '#src/services/presence'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import Highlights from '#src/components/ExpertProfile/Highlights'
import Photo from '#src/components/ExpertProfile/PhotoCarousel'
import Status from '#src/components/ExpertProfile/Status'
import Body from '#src/components/ExpertProfile/Body'
import FollowMe from '#src/components/ExpertProfile/FollowMe'
import styles from './styles.css'

interface ExpertIntroProps {
  lead: DepartmentLeadFragment
  setConsumerInitialLeadAction: SetConsumerInitialLeadActionMutationFn
}

class ExpertIntroInline extends React.PureComponent<ExpertIntroProps & WithAnalyticsProps> {
  public componentDidMount() {
    this.props
      .setConsumerInitialLeadAction({
        variables: {
          action: ConsumerInitialLeadAction.CHAT,
          leadId: this.props.lead.id
        }
      })
      .then(() => {
        if (this.props.analytics) {
          this.props.analytics.events.pathExited({ destination: 'chat' })
        }
      })
  }

  public render() {
    const expertBusinessUser = this.props.lead.expert

    // We hide this section for placeholder experts
    if (
      expertBusinessUser.expertAttributes &&
      expertBusinessUser.expertAttributes.placeholderExpert
    ) {
      return null
    }

    const areasDefaultTopSection = generateGrid([
      {
        photo: 2
      },
      {
        headline: 2
      },
      {
        body: 2
      },
      {
        followme: 2
      },
      {
        border: 2
      },
      {
        status: 2
      },
      {
        highlights: 2
      },
      {
        viewProfile: 2
      }
    ])

    const areasLgTopSection = generateGrid([
      {
        photo: 4,
        headline: 8
      },
      {
        photo: 4,
        body: 8
      },
      {
        photo: 4,
        followme: 8
      },
      {
        photo: 4,
        status: 8
      },
      {
        photo: 4,
        highlights: 8
      },
      {
        photo: 4,
        viewProfile: 8
      }
    ])

    const areasXxlTopSection = generateGrid([
      {
        photo: 4,
        headline: 8
      },
      {
        photo: 4,
        body: 8
      },
      {
        photo: 4,
        followme: 8
      },
      {
        photo: 4,
        status: 8
      },
      {
        photo: 4,
        highlights: 8
      },
      {
        photo: 4,
        viewProfile: 8
      }
    ])

    const expertInstagram =
      expertBusinessUser.expertProfile &&
      expertBusinessUser.expertProfile.socialMediaLinks.find(
        SocialMediaLink => SocialMediaLink.socialMediaType === SocialMediaType.INSTAGRAM
      )

    return (
      <div className={styles.module}>
        <SidebarAwareGrid>
          <GridItem col={2}>
            <div className={styles.moduleHeader}>Meet your expert</div>
          </GridItem>
        </SidebarAwareGrid>
        <SidebarAwareGrid
          areas={areasDefaultTopSection}
          areasLg={areasLgTopSection}
          areasXxlg={areasXxlTopSection}
        >
          {({
            Photo: PhotoArea,
            Headline,
            Body: BodyArea,
            Followme: FollowMeArea,
            Status: StatusArea,
            Highlights: HighlightsArea,
            ViewProfile: ViewProfileArea
          }) => (
            <>
              <PhotoArea>
                <Photo
                  expertProfile={expertBusinessUser.expertProfile}
                  className={styles.imageCarousel}
                />
              </PhotoArea>

              <Headline>
                <h1 className={styles.expertName}>{expertBusinessUser.displayName}</h1>
              </Headline>

              <BodyArea>
                {expertBusinessUser.expertProfile && (
                  <Body
                    className={styles.darkText}
                    body={expertBusinessUser.expertProfile.background}
                  />
                )}
              </BodyArea>

              <FollowMeArea>
                {expertInstagram && <FollowMe instagramUrl={expertInstagram.url} />}
              </FollowMeArea>

              <StatusArea>
                <Status
                  className={styles.darkText}
                  expertId={expertBusinessUser.id}
                  totalReviews={
                    expertBusinessUser.expertStats && expertBusinessUser.expertStats.totalReviews
                  }
                  averageRating={
                    expertBusinessUser.expertStats && expertBusinessUser.expertStats.averageRating
                  }
                  acceptingNewRequests={isExpertPresent(expertBusinessUser)}
                  suppressReviewsAndFeed={false}
                />
              </StatusArea>

              <HighlightsArea>
                <Highlights
                  departmentSlug={expertBusinessUser.department.slug}
                  className={styles.darkText}
                  highlights={
                    expertBusinessUser.expertProfile && expertBusinessUser.expertProfile.highlights
                  }
                  headline={
                    expertBusinessUser.expertProfile && expertBusinessUser.expertProfile.headline
                  }
                  totalLeads={
                    expertBusinessUser.expertStats &&
                    expertBusinessUser.expertStats.totalLeadsAsPrimaryExpert
                  }
                  invertIcons={true}
                />
              </HighlightsArea>
              <ViewProfileArea className={styles.viewProfile}>
                <Link to={`/experts/${expertBusinessUser.vanityId || expertBusinessUser.id}`}>
                  <Button variant="contrast-outline" size="medium">
                    View full profile
                  </Button>
                </Link>
              </ViewProfileArea>
            </>
          )}
        </SidebarAwareGrid>
      </div>
    )
  }
}

interface ExpertIntroExperimentProps extends RouteComponentProps<{}> {
  lead: DepartmentLeadFragment
}

const ExpertIntroExperiment: React.SFC<ExpertIntroExperimentProps & WithAnalyticsProps> = ({
  lead,
  history,
  analytics
}) => {
  const expertIntroductionParam = new URLSearchParams(history.location.search).has(
    'expert-introduction'
  )
  const expertBusinessUser = lead.expert
  if (!expertIntroductionParam || !expertBusinessUser) {
    return null
  }

  return (
    <SetConsumerInitialLeadActionComponent>
      {setConsumerInitialLeadAction => (
        <ExpertIntroInline
          analytics={analytics}
          setConsumerInitialLeadAction={setConsumerInitialLeadAction}
          lead={lead}
        />
      )}
    </SetConsumerInitialLeadActionComponent>
  )
}

export default withAnalytics(withRouter(ExpertIntroExperiment))
